import { z } from 'zod';

import { VoucherViewSchema } from '../traveler/traveler_view';
import { HhMmSchema, IataSchema, MiceMoneySchema, MiceNamedRefSchema } from './mice_refs';
export {
  HhMmSchema,
  IataSchema,
  MiceMoneySchema,
  MiceNamedRefSchema,
  type MiceMoney,
  type MiceNamedRef,
} from './mice_refs';
import { MiceProfileCheckViewSchema } from './mice_profile';
import { MiceEventRuleSchema } from './mice_event_rules';
import { EMPTY_MICE_PLACES, MiceEventPlacesSchema } from './mice_places';
import { EMPTY_PARTICIPANT_COUNTS, MiceParticipantCountsSchema } from './mice_participants';
import { MiceRsvpModeSchema } from './mice_estimate';
import { MiceQuestionSchema } from './mice_questions';
import { MiceRoomsSummarySchema } from './mice_rooms';
import { MiceTransferRuleSchema, MiceTransferSummarySchema } from './mice_transfer';

/** Fio web ↔ mice-api (spec 2026-10-15-mice-compra-em-lote). */

export const MICE_ORDER_STATUSES = [
  'validating',
  'invalid',
  'duplicate',
  'ready',
  'queued',
  'purchasing',
  'awaiting_approval',
  'awaiting_issuance',
  'issued',
  'failed',
  'indeterminate',
] as const;
export const MiceOrderStatusSchema = z.enum(MICE_ORDER_STATUSES);
export type MiceOrderStatus = z.infer<typeof MiceOrderStatusSchema>;

/** Status em que o pedido ainda anda sozinho; a tela faz polling enquanto houver algum. */
export const MICE_IN_FLIGHT_STATUSES: readonly MiceOrderStatus[] = [
  'validating',
  'queued',
  'purchasing',
  'awaiting_approval',
  'awaiting_issuance',
];

/** Status em que o pedido ainda não entrou na compra: só nele o pedido (e o evento) pode ser apagado. */
export const MICE_DELETABLE_STATUSES: readonly MiceOrderStatus[] = [
  'validating',
  'invalid',
  'duplicate',
  'ready',
  'failed',
];

export const MiceFlightLegSchema = z.object({
  date: z.iso.date(),
  carrier: z.string().regex(/^[A-Z0-9]{2}$/),
  flightNumber: z.string().regex(/^\d{1,4}$/),
});
export type MiceFlightLeg = z.infer<typeof MiceFlightLegSchema>;

export const MiceOutboundSchema = MiceFlightLegSchema.extend({
  origin: IataSchema,
  destination: IataSchema,
});
export type MiceOutbound = z.infer<typeof MiceOutboundSchema>;

export const MiceVoucherRefSchema = z.object({
  direction: z.enum(['OUTWARD', 'RETURN']),
  voucherId: z.string(),
  voucherCode: z.string().nullable(),
  locator: z.string().nullable(),
  status: z.string().nullable(),
});
export type MiceVoucherRef = z.infer<typeof MiceVoucherRefSchema>;

export const MiceOrderErrorSchema = z.object({ code: z.string(), message: z.string() });
export type MiceOrderError = z.infer<typeof MiceOrderErrorSchema>;

/** Erros da validação que vêm do e-mail da linha: só eles aceitam a correção do e-mail. */
export const MICE_EMAIL_FIXABLE_ERROR_CODES = [
  'traveler_not_found',
  'traveler_other_company',
] as const;

/** Linha (necessidade ou pedido) `invalid` por um erro do e-mail: corrigir a devolve à validação. */
export const isEmailFixable = (row: { status: string; error: MiceOrderError | null }): boolean =>
  row.status === 'invalid' &&
  (MICE_EMAIL_FIXABLE_ERROR_CODES as readonly string[]).includes(row.error?.code ?? '');

export const MiceCorrectEmailRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email().max(320)),
});
export type MiceCorrectEmailRequest = z.infer<typeof MiceCorrectEmailRequestSchema>;

export const MiceOrderViewSchema = z.object({
  id: z.string(),
  rowNumber: z.number().int().positive(),
  raw: z.record(z.string(), z.string()),
  travelerEmail: z.string(),
  travelerName: z.string().nullable(),
  outbound: MiceOutboundSchema.nullable(),
  inbound: MiceFlightLegSchema.nullable(),
  status: MiceOrderStatusSchema,
  error: MiceOrderErrorSchema.nullable(),
  vouchers: z.array(MiceVoucherRefSchema),
  pricePaid: MiceMoneySchema.nullable(),
  /** Sentido do pedido gerado por necessidade; `null` = pedido de planilha (ida, ou ida e volta). */
  direction: z.enum(['OUTWARD', 'RETURN']).nullable(),
  /** Necessidade que gerou o pedido; `null` = pedido de planilha. */
  needId: z.string().nullable(),
  /** Cotação que passou do teto (`price_drift`); `null` enquanto nenhuma passou. */
  lastQuote: z.object({ price: MiceMoneySchema, at: z.iso.datetime() }).nullable(),
  /** `null`: a validação do cadastro ainda não rodou (ou o documento é anterior a ela). */
  profileCheck: MiceProfileCheckViewSchema.nullable(),
  queuedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});
export type MiceOrderView = z.infer<typeof MiceOrderViewSchema>;

export const MiceStatusCountsSchema = z.record(
  MiceOrderStatusSchema,
  z.number().int().nonnegative(),
);
export type MiceStatusCounts = z.infer<typeof MiceStatusCountsSchema>;

export function emptyMiceStatusCounts(): MiceStatusCounts {
  return Object.fromEntries(MICE_ORDER_STATUSES.map((s) => [s, 0])) as MiceStatusCounts;
}

/** Fase da jornada do viajante: progresso e filtro da página do evento. Fica aqui (e não em
 *  mice_travelers.ts) porque o `MiceProjectViewSchema` conta por fase. */
export const MicePhaseSchema = z.enum([
  'preparation',
  'agent',
  'approval',
  'buying',
  'issued',
  'attention',
  'manual',
]);
export type MicePhase = z.infer<typeof MicePhaseSchema>;

/** Fases da barra, na ordem; `manual` só entra em "Todos". */
export const MICE_PHASE_BAR: readonly MicePhase[] = [
  'preparation',
  'agent',
  'approval',
  'buying',
  'issued',
  'attention',
];

/** Filtro de fase da lista: uma fase ou todas. */
export const MicePhaseFilterSchema = z.union([MicePhaseSchema, z.literal('all')]);
export type MicePhaseFilter = z.infer<typeof MicePhaseFilterSchema>;

/** Quantos viajantes em cada fase, mais `all`. */
export const MicePhaseCountsSchema = z.record(
  MicePhaseFilterSchema,
  z.number().int().nonnegative(),
);
export type MicePhaseCounts = z.infer<typeof MicePhaseCountsSchema>;

export const MiceRequesterSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string(),
});

/** De onde vem o solicitante: o roster do workspace ou o do cliente interno da VOLL (o agente
 *  VOLL compra no workspace com o próprio id, como no `signinInClient` do app legado). */
export const MiceRequesterOriginSchema = z.enum(['client', 'voll']);
export type MiceRequesterOrigin = z.infer<typeof MiceRequesterOriginSchema>;

/** Regra de um aeroporto de destino do evento. `lastResort`: só entra se nada melhor couber. */
export const MiceAirportRuleSchema = z.object({
  iata: IataSchema,
  arrivalIdeal: HhMmSchema.nullable(),
  arrivalTolerated: HhMmSchema.nullable(),
  lastResort: z.boolean(),
});
export type MiceAirportRule = z.infer<typeof MiceAirportRuleSchema>;

/** Janela de embarque da volta: a partir de `earliestIdeal`; `earliestTolerated` (mais cedo) ainda
 *  serve, como tolerância; antes dele (ou do ideal, sem tolerado) fica fora. Com `date`, vale só para
 *  a volta que embarca nesse dia (quem volta em outra data fica livre); sem, para toda volta. */
export const MiceReturnDepartureSchema = z.object({
  date: z.iso.date().nullable(),
  earliestIdeal: HhMmSchema,
  earliestTolerated: HhMmSchema.nullable(),
});
export type MiceReturnDeparture = z.infer<typeof MiceReturnDepartureSchema>;

/** Regra de quartos do evento: ocupação, mistura de gênero, hotel e noite extra (quem chega cedo). */
export const MiceRoomingSchema = z.object({
  occupancy: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  genderRule: z.enum(['same_gender', 'any']),
  /** Legado: o hotel mora em `places` (mice_places.ts) e esta chave sai num deploy seguinte. Tasks
   *  antigas exigem a chave, por isso ela continua, sempre `null`. Um objeto legado (gravado por
   *  um mice antigo no deploy escalonado, ou antes do backfill) é lido como `null`. */
  hotel: z.null().catch(null).default(null),
  /** Chegando antes deste horário, o viajante precisa da diária do dia anterior. */
  extraNightWhenArrivingBefore: HhMmSchema.nullable(),
  notes: z.string(),
});
export type MiceRooming = z.infer<typeof MiceRoomingSchema>;

/** Premissas estruturadas do evento. Ficam aqui (e não em mice_needs.ts) porque o
 *  `MiceProjectViewSchema` as usa e mice_needs.ts importa deste arquivo. */
export const MiceEventPremisesSchema = z.object({
  destinations: z.array(MiceAirportRuleSchema).min(1),
  /** Madrugada: início inclusivo, fim exclusivo. `start > end` cruza a meia-noite (23:00-05:00);
   *  `start === end` = sem janela. */
  redEye: z.object({ start: HhMmSchema, end: HhMmSchema }),
  /** Exceção da madrugada: chegada até este horário (inclusive), com embarque fora dela, vale como
   *  último recurso. Nulo = sem exceção (também nas premissas salvas antes dela). */
  redEyeArrivalUntil: HhMmSchema.nullable().default(null),
  preferredDepartureFrom: HhMmSchema.nullable(),
  /** Nulo nas premissas salvas antes da janela da volta existir. */
  returnDeparture: MiceReturnDepartureSchema.nullable().default(null),
  checkedBaggage: z.boolean(),
  /** Sem voo do dia do evento que chegue no horário, pode chegar no dia anterior. Falso nas
   *  premissas salvas antes dele. */
  arrivalDayBefore: z.boolean().default(false),
  savingsReviewThreshold: MiceMoneySchema.nullable(),
  /** Nulo nas premissas salvas antes dos quartos existirem. */
  rooming: MiceRoomingSchema.nullable().default(null),
  /** Transfer do evento; nulo = sem transfer (também nas premissas salvas antes dele). */
  transfer: MiceTransferRuleSchema.default(null),
  notes: z.string(),
});
export type MiceEventPremises = z.infer<typeof MiceEventPremisesSchema>;

/** Rascunho das premissas (evento self-service): qualquer subconjunto delas. Os campos com
 *  `.default(null)` entram sem o default — o rascunho não inventa chaves que ninguém preencheu. */
export const MiceEventPremisesDraftSchema = z
  .object({
    ...MiceEventPremisesSchema.shape,
    redEyeArrivalUntil: HhMmSchema.nullable().optional(),
    arrivalDayBefore: z.boolean().optional(),
    returnDeparture: MiceReturnDepartureSchema.nullable().optional(),
    rooming: MiceRoomingSchema.nullable().optional(),
    transfer: MiceTransferRuleSchema.optional(),
  })
  .partial();
export type MiceEventPremisesDraft = z.infer<typeof MiceEventPremisesDraftSchema>;

export const MICE_PREMISES_KEYS = Object.keys(
  MiceEventPremisesSchema.shape,
) as unknown as readonly [keyof MiceEventPremises, ...(keyof MiceEventPremises)[]];
export const MicePremisesKeySchema = z.enum(MICE_PREMISES_KEYS);
export type MicePremisesKey = z.infer<typeof MicePremisesKeySchema>;

/** Quem criou o evento: o operador (backoffice) ou o organizador do cliente (self-service). */
export const MiceProjectOriginSchema = z.enum(['operator', 'self_service']);
export type MiceProjectOrigin = z.infer<typeof MiceProjectOriginSchema>;

export const MiceOrganizerSchema = z.object({
  userId: z.string().regex(/^\d+$/),
  clientId: z.string().regex(/^\d+$/),
  clientName: z.string().min(1).max(160),
  email: z.email(),
  name: z.string().nullable(),
});
export type MiceOrganizer = z.infer<typeof MiceOrganizerSchema>;

/** De onde vem o centro de custo/motivo: o evento inteiro, cada viajante na importação ou as regras do viajante na VOLL. */
export const MICE_CORPORATE_PATHS = ['event', 'per_traveler', 'traveler_rules'] as const;
export const MiceCorporatePathSchema = z.enum(MICE_CORPORATE_PATHS);
export type MiceCorporatePath = z.infer<typeof MiceCorporatePathSchema>;

/** Os três dados corporativos que o evento pode deixar com o viajante. */
export const MICE_CORPORATE_FIELDS = ['costCenter', 'motive', 'project'] as const;
export const MiceCorporateFieldSchema = z.enum(MICE_CORPORATE_FIELDS);
export type MiceCorporateField = z.infer<typeof MiceCorporateFieldSchema>;

export const MiceProjectViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  company: MiceNamedRefSchema,
  requester: MiceRequesterSchema,
  /** Nulo quando o caminho não é `event`. */
  costCenter: MiceNamedRefSchema.nullable(),
  motive: MiceNamedRefSchema.nullable(),
  // Default: o web lê a view de um mice-api antigo (sempre `event`) no rolling deploy.
  costCenterPath: MiceCorporatePathSchema.default('event'),
  motivePath: MiceCorporatePathSchema.default('event'),
  corporateProject: MiceNamedRefSchema.nullable(),
  // Default: o web lê a view de um mice-api antigo (sempre `event`, sem exigir projeto) no rolling deploy.
  corporateProjectPath: MiceCorporatePathSchema.default('event'),
  /** O cliente exige projeto e a empresa de débito tem projetos: gravado na criação. */
  projectRequired: z.boolean().default(false),
  /** Grupo corporativo do workspace (`company`); nulo nos projetos criados antes dele. */
  client: MiceNamedRefSchema.nullable(),
  /** Empresa de débito (rateio) do centro de custo. */
  debitCompany: MiceNamedRefSchema,
  counts: MiceStatusCountsSchema,
  /** Viajantes por fase (a mesma junção da leitura de viajantes). */
  phaseCounts: MicePhaseCountsSchema,
  /** `MICE_PRICE_DRIFT_TOLERANCE`: o teto de cada bilhete é a tarifa × (1 + isto). */
  priceTolerance: z.number().min(0).max(1),
  /** Início do evento (YYYY-MM-DD). */
  eventDate: z.iso.date().nullable(),
  /** Fim do evento; nulo nos projetos criados antes do período. */
  eventEnd: z.iso.date().nullable(),
  premises: MiceEventPremisesSchema.nullable(),
  premisesText: z.string().nullable(),
  /** Quando o operador conferiu as premissas; nulo = recomendar fica travado. */
  premisesCheckedAt: z.iso.datetime().nullable(),
  /** Quando o operador pediu o orçamento do evento; nulo = nunca pedido. */
  budgetRequestedAt: z.iso.datetime().nullable().default(null),
  // Default: o web valida a resposta de um mice-api antigo durante o rolling deploy.
  origin: MiceProjectOriginSchema.default('operator'),
  /** Organizador do cliente; nulo nos eventos criados pelo operador. */
  organizer: MiceOrganizerSchema.nullable().default(null),
  /** Conversa do chat em que o evento nasceu (self-service). */
  conversationId: z.string().nullable().default(null),
  /** Premissas ainda em rascunho (self-service), antes de confirmadas. */
  premisesDraft: MiceEventPremisesDraftSchema.nullable().default(null),
  /** Chaves das premissas que o organizador fixou à mão (a extração não as sobrescreve). */
  premisesPinned: z.array(MicePremisesKeySchema).default([]),
  /** Regras de viagem que ninguém disse nem editou ainda (a ficha as destaca). */
  rulesPending: z.array(MiceEventRuleSchema).default([]),
  /** Chaves que ainda faltam para confirmar o evento. */
  missingPremises: z.array(MicePremisesKeySchema).default([]),
  /** Com as regras confirmadas, o rascunho tem mudança ainda não salva: as regras em vigor seguem
   *  valendo até "Salvar alterações". Default: view de um mice-api antigo. */
  premisesChanged: z.boolean().default(false),
  /** Perguntas do evento ao convidado (arquivadas também). Default: view de um mice-api antigo. */
  questions: z.array(MiceQuestionSchema).default([]),
  /** O convite compra os voos (`purchase`) ou só levanta presença, saída e respostas (`survey`).
   *  Default: view de um mice-api antigo. */
  rsvpMode: MiceRsvpModeSchema.default('purchase'),
  /** Compras do evento encerradas pelo organizador: nenhum pedido, recomendação ou compra nova.
   *  Default: quem lê a view de um mice-api antigo no rolling deploy vê compras abertas. */
  purchasesClosedAt: z.iso.datetime().nullable().default(null),
  /** Local e hotéis do evento; opcionais e fora das regras. Default: view de um mice-api antigo. */
  places: MiceEventPlacesSchema.default(EMPTY_MICE_PLACES),
  /** Quando o organizador publicou o evento; nulo = ainda em preparação. */
  publishedAt: z.string().nullable().default(null),
  /** Contagens da lista de participantes. */
  participants: MiceParticipantCountsSchema.default(EMPTY_PARTICIPANT_COUNTS),
  /** Resumo dos quartos (duplas e sem dupla); nulo = evento sem quartos. Default: view de um mice-api antigo. */
  rooms: MiceRoomsSummarySchema.nullable().default(null),
  transfer: MiceTransferSummarySchema.nullable().default(null),
  /** Prazo de resposta do convite (YYYY-MM-DD); nulo = ainda sem convites enviados. */
  inviteDeadline: z.string().nullable().default(null),
  /** Quando o organizador arquivou o evento; nulo = ativo. Default: view de um mice-api antigo. */
  archivedAt: z.iso.datetime().nullable().default(null),
  /** Há pedido fora de `MICE_DELETABLE_STATUSES` (o que impede apagar). Default: view de um mice-api antigo. */
  hasPurchase: z.boolean().default(false),
  createdAt: z.iso.datetime(),
});
export type MiceProjectView = z.infer<typeof MiceProjectViewSchema>;

export const MiceProjectListSchema = z.object({ items: z.array(MiceProjectViewSchema) });

/** Busca de cliente/solicitante: texto livre, a partir de 2 letras. */
export const MiceSearchQuerySchema = z.object({ q: z.string().trim().min(2).max(120) });

/** Busca de solicitante: além do texto, o grupo corporativo (`clientId`) dono do workspace. */
export const MiceRequesterSearchQuerySchema = MiceSearchQuerySchema.extend({
  clientId: z.string().min(1),
});

/** Cliente = grupo corporativo; `workspaces` = os ambientes (`client_id`) dele. */
export const MiceClientOptionSchema = MiceNamedRefSchema.extend({
  workspaces: z.array(MiceNamedRefSchema),
});
export type MiceClientOption = z.infer<typeof MiceClientOptionSchema>;
export const MiceClientOptionsSchema = z.object({ items: z.array(MiceClientOptionSchema) });

/** O que só depende do workspace: as empresas de débito e os motivos. */
export const MiceWorkspaceOptionsSchema = z.object({
  debitCompanies: z.array(MiceNamedRefSchema),
  motives: z.array(MiceNamedRefSchema),
});
export type MiceWorkspaceOptions = z.infer<typeof MiceWorkspaceOptionsSchema>;

/** O que depende da empresa de débito: centros de custo e projetos corporativos. */
export const MiceDebitCompanyOptionsSchema = z.object({
  costCenters: z.array(MiceNamedRefSchema),
  projects: z.array(MiceNamedRefSchema),
});
export type MiceDebitCompanyOptions = z.infer<typeof MiceDebitCompanyOptionsSchema>;

export const MiceRequesterOptionSchema = MiceRequesterSchema.extend({
  origin: MiceRequesterOriginSchema,
});
export type MiceRequesterOption = z.infer<typeof MiceRequesterOptionSchema>;
export const MiceRequesterOptionsSchema = z.object({ items: z.array(MiceRequesterOptionSchema) });

const IdSchema = z.string().min(1);

/** Id presente ⇔ caminho `event`, por campo. No projeto o caminho `event` sem id é válido
 *  ("Sem projeto"): só o id fora do `event` é recusado; sem caminho vale `event`. */
export function refineCorporatePaths(
  body: {
    costCenterId?: string;
    costCenterPath: MiceCorporatePath;
    motiveId?: string;
    motivePath: MiceCorporatePath;
    corporateProjectId?: string;
    corporateProjectPath?: MiceCorporatePath;
  },
  ctx: z.RefinementCtx,
): void {
  const fields = [
    ['costCenterId', body.costCenterId, body.costCenterPath],
    ['motiveId', body.motiveId, body.motivePath],
  ] as const;
  for (const [field, id, path] of fields) {
    const hasId = id !== undefined;
    if (hasId === (path === 'event')) continue;
    ctx.addIssue({
      code: 'custom',
      path: [field],
      message: hasId ? 'O id só vale no caminho event' : 'O caminho event exige o id',
    });
  }
  if (body.corporateProjectId !== undefined && (body.corporateProjectPath ?? 'event') !== 'event') {
    ctx.addIssue({
      code: 'custom',
      path: ['corporateProjectId'],
      message: 'O id só vale no caminho event',
    });
  }
}

export const CreateMiceProjectRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    eventStart: z.iso.date(),
    eventEnd: z.iso.date(),
    clientId: IdSchema,
    workspaceId: IdSchema,
    debitCompanyId: IdSchema,
    costCenterId: IdSchema.optional(),
    costCenterPath: MiceCorporatePathSchema.default('event'),
    motiveId: IdSchema.optional(),
    motivePath: MiceCorporatePathSchema.default('event'),
    /** Da empresa de débito escolhida. */
    corporateProjectId: IdSchema.optional(),
    /** O e-mail é a chave da busca com que o servidor reconfirma o id no roster da origem. */
    requester: z.object({ id: IdSchema, email: z.email(), origin: MiceRequesterOriginSchema }),
  })
  .refine((body) => body.eventEnd >= body.eventStart, {
    path: ['eventEnd'],
    message: 'O fim do evento não pode ser antes do início',
  })
  .superRefine(refineCorporatePaths);
export type CreateMiceProjectRequest = z.infer<typeof CreateMiceProjectRequestSchema>;

export const MiceOrdersQuerySchema = z.object({
  status: MiceOrderStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type MiceOrdersQuery = z.infer<typeof MiceOrdersQuerySchema>;

export const MiceOrdersPageSchema = z.object({
  items: z.array(MiceOrderViewSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type MiceOrdersPage = z.infer<typeof MiceOrdersPageSchema>;

export const MiceOrderIdsRequestSchema = z.object({
  orderIds: z.array(z.string().min(1)).min(1).max(500),
});

export const MiceOrderActionResultSchema = z.object({ updated: z.number().int().nonnegative() });

export const MiceOrderVouchersSchema = z.object({
  /** Os vouchers vinculados ao pedido, ida antes da volta. */
  items: z.array(VoucherViewSchema),
  /** Os outros aéreos vivos do viajante no período do pedido (ida − 1 a volta + 1): a operação
   *  confere sem procurar na plataforma (#6007). Default para a API anterior ao campo. */
  otherLive: z.array(VoucherViewSchema).default([]),
});
export type MiceOrderVouchers = z.infer<typeof MiceOrderVouchersSchema>;
